import { FC, useCallback, useEffect } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { CareCreditPageImage } from '@frontend/payments-assets';
import { useCareCreditLocationProviderStatus, useCareCreditLocationStatus } from '@frontend/payments-care-credit';
import { getPaymentsBillingManagerLocations } from '@frontend/payments-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useEligibility } from '@frontend/user-preferences';
import { PromoBannerBadge } from '../../../common/components/promo-banners/promo-banner-badge';
import { CareCreditPLGPromoTrackingIds } from '../../tracking-ids';
import { PaymentsPromoBanner } from '../common/payments-promo-banner';

interface CareCreditPLGPromotionBannerProps {
  onClose?: () => void;
  innerRef?: React.LegacyRef<HTMLDivElement>;
  containerStyles?: Interpolation<Theme>;
}

const styles = {
  banner: css`
    .payments-promo-banner__right-col {
      padding-bottom: var(--fabric-space-padding-md);
    }
  `,
  featuredImageStyles: css`
    justify-self: flex-end;
    margin-top: -48px;
    margin-right: var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-md);
    box-shadow: 0 4px 8px 3px #00215226;
  `,
};

const CARE_CREDIT_LEARN_MORE_LINK = 'https://www.weavehelp.com/weavehelp/article/connecting-and-using-carecredit';
const MAX_DISMISSALS = 3;

export const CareCreditPLGPromotionBanner: FC<CareCreditPLGPromotionBannerProps> = ({
  innerRef,
  onClose,
  containerStyles,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'payments' });
  const { selectedLocationIds } = useAppScopeStore();
  const { navigate } = useSettingsNavigate();

  const { isCareCreditLoading, isCareCreditEnabled } = useCareCreditLocationStatus(selectedLocationIds[0]);

  const { isProviderConnected, isProviderLoading } = useCareCreditLocationProviderStatus(
    { locationId: selectedLocationIds[0] },
    { enabled: isCareCreditEnabled }
  );

  const {
    shouldShow: shouldShowBanner,
    recordDismiss,
    recordClick,
  } = useEligibility({
    featureId: 'payments--care-credit-plg-banner',
    defaultCriteria: {
      type: 'dismiss-cooldown',
      maxDismisses: MAX_DISMISSALS,
      cooldownDays: 1,
    },
  });

  const { isPaymentsBillingManager } = getPaymentsBillingManagerLocations({
    locationIds: selectedLocationIds,
    strategy: 'any',
  });

  const handleMaybeLater = useCallback(() => {
    recordDismiss();
    onClose?.();
  }, [recordDismiss, onClose]);

  const handleGetStarted = useCallback(() => {
    navigate({ to: '/payments/care-credit' });
    recordClick();
  }, [navigate, recordClick]);

  const handleLearnMore = useCallback(() => {
    window.open(CARE_CREDIT_LEARN_MORE_LINK, '_blank', 'noopener,noreferrer');
  }, []);

  const isLoading = isCareCreditLoading || isProviderLoading;

  useEffect(() => {
    if (!isLoading) {
      if (isProviderConnected || !isCareCreditEnabled) {
        /**
         * Indicate to the priority renderer that this banner is not visible/relevant
         */
        onClose?.();
      }
    }
  }, [isLoading, isProviderConnected, isCareCreditEnabled]);

  if (isLoading || isProviderConnected || !shouldShowBanner || !isCareCreditEnabled || !isPaymentsBillingManager) {
    return null;
  }

  return (
    <div ref={innerRef} css={[styles.banner, containerStyles]}>
      <PaymentsPromoBanner
        headerText={t('Offer Flexible Financing with CareCredit')}
        HeaderPrefix={() => <PromoBannerBadge badgeType='new' />}
        titleText={t(
          'CareCredit is now available in Weave. Give customers the option to pay for treatments while helping you collect payments faster.'
        )}
        descriptionText={t(
          'You’ll need your Merchant ID Number and either your practice code or last 4 digits of your bank account number to get started.'
        )}
        FeatureImage={CareCreditPageImage}
        featureImageStyles={styles.featuredImageStyles}
        footerActions={{
          primary: {
            text: t('Get Started'),
            trackingId: CareCreditPLGPromoTrackingIds.setUpCareCredit,
            onClick: handleGetStarted,
          },
          secondary: {
            text: t('Learn More'),
            trackingId: CareCreditPLGPromoTrackingIds.learnMore,
            onClick: handleLearnMore,
          },
          tertiary: {
            text: t('Maybe Later'),
            trackingId: CareCreditPLGPromoTrackingIds.maybeLater,
            onClick: handleMaybeLater,
          },
        }}
      />
    </div>
  );
};
